import { Point } from '@/types';

/**
 * Architectural dimension geometry.
 *
 * A dimension is defined by three points: p1 and p2 are the measured endpoints,
 * and p3 sets the perpendicular OFFSET at which the dimension line + label sit
 * (its distance and side from the p1->p2 line). This derives the offset
 * dimension line a1..a2 (parallel to p1->p2) plus the unit normal pointing from
 * the measured points toward that line. Works in any linear space (page or
 * zoom-scaled), so the renderer and hit-testing share one source of truth.
 */
export interface DimensionGeometry {
  a1: Point;      // dimension-line end above p1
  a2: Point;      // dimension-line end above p2
  nx: number;     // unit normal x (base -> dimension line)
  ny: number;     // unit normal y
  offset: number; // signed perpendicular distance of p3 from the p1->p2 line
}

const TICK_LEN = 6;
const WITNESS_GAP = 2;
const WITNESS_OVER = 4;

/**
 * The stroke segments that make up a dimension's line work (witness lines, the
 * offset dimension line, and the two arch ticks) for a given point list, in the
 * SAME linear space as the points. Shared by the PDF export (flatten + appearance
 * streams) so the baked geometry matches the on-canvas renderer. Returns just the
 * A->B baseline when the offset point is not yet present.
 */
export function dimensionSegments(points: Point[]): [Point, Point][] {
  if (points.length < 2) return [];
  const [p1, p2, p3] = points;
  if (!p3) return [[p1, p2]];
  const { a1, a2, nx, ny, offset } = dimensionGeometry(p1, p2, p3);
  const sign = offset >= 0 ? 1 : -1;
  const off = (p: Point, k: number): Point => ({ x: p.x + nx * sign * k, y: p.y + ny * sign * k });
  const dx = a2.x - a1.x, dy = a2.y - a1.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len;
  const sx = (ux - uy) / Math.SQRT2, sy = (uy + ux) / Math.SQRT2;
  const tick = (pt: Point): [Point, Point] => [
    { x: pt.x - sx * TICK_LEN, y: pt.y - sy * TICK_LEN },
    { x: pt.x + sx * TICK_LEN, y: pt.y + sy * TICK_LEN },
  ];
  return [
    [off(p1, WITNESS_GAP), off(a1, WITNESS_OVER)],
    [off(p2, WITNESS_GAP), off(a2, WITNESS_OVER)],
    [a1, a2],
    tick(a1),
    tick(a2),
  ];
}

/** The dimension line's midpoint at a signed perpendicular `offset` from A->B (the left-hand normal's side). */
export function dimensionOffsetPoint(p1: Point, p2: Point, offset: number): Point {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: (p1.x + p2.x) / 2 - (dy / len) * offset, y: (p1.y + p2.y) / 2 + (dx / len) * offset };
}

/** How far the derived offset puts the dimension line off A->B, in SCREEN px (Decision 7: about 12). */
export const DIMENSION_DERIVED_OFFSET_PX = 12;

/**
 * The offset point a dimension placed by A and B alone gets (owner Decision 7, spec P5 `derive`): on the dimension
 * line's midpoint, `DIMENSION_DERIVED_OFFSET_PX` screen px off A->B on the READING-UP side (the side its label
 * reads toward, `dimensionLabelLayout`), at the live `zoom` (CSS px per page unit). Move it with the offset handle.
 */
export function deriveDimensionOffset(a: Point, b: Point, zoom: number): Point {
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const up = dimensionLabelLayout(a, b, 0, { gap: 1, endClear: 0, yAxis: 'down' });
  const d = DIMENSION_DERIVED_OFFSET_PX / (zoom || 1);
  return { x: mid.x + (up.x - mid.x) * d, y: mid.y + (up.y - mid.y) * d };
}

/**
 * A dimension's points with the offset point normalised onto the dimension line's MIDPOINT (same side, same
 * distance). The stored third point is then where its handle is drawn -- on the line, never floating at the raw
 * click -- and it is the one canonical form create and every edit write. Fewer than 3 points pass through.
 */
export function normalizeDimensionPoints(points: Point[]): Point[] {
  if (points.length < 3) return points;
  const [p1, p2, p3] = points;
  return [p1, p2, dimensionOffsetPoint(p1, p2, dimensionGeometry(p1, p2, p3).offset)];
}

/**
 * A dimension after one of its three handles moved. A or B: the dimension line keeps its signed offset (distance
 * and side relative to A->B), so it follows the measured points instead of re-deriving its side from a fixed
 * offset point. The offset handle: the line moves to the dragged point's perpendicular distance. The result is
 * always normalised (`normalizeDimensionPoints`).
 */
export function moveDimensionHandle(points: Point[], handleIndex: number, point: Point): Point[] {
  if (points.length < 3) {
    const out = [...points];
    if (handleIndex >= 0 && handleIndex < out.length) out[handleIndex] = point;
    return out;
  }
  const [p1, p2, p3] = points;
  if (handleIndex === 2) return normalizeDimensionPoints([p1, p2, point]);
  if (handleIndex !== 0 && handleIndex !== 1) return points;
  const { offset } = dimensionGeometry(p1, p2, p3);
  const a = handleIndex === 0 ? point : p1;
  const b = handleIndex === 1 ? point : p2;
  return [a, b, dimensionOffsetPoint(a, b, offset)];
}

/** Which way the space's y axis points: the canvas / page space is y-down, PDF user space is y-up. */
export type YAxis = 'down' | 'up';

export interface DimensionLabelLayout {
  /** The text anchor on its baseline: the CENTRE for `middle`, the start (left end) for `start`. */
  x: number;
  y: number;
  /** Reading angle in radians, in the space's own sense (atan2 of the line), folded so the text is upright. */
  angle: number;
  anchor: 'middle' | 'start';
  /** The label did not fit between the ticks and sits past the end of the line. */
  outside: boolean;
}

/**
 * Where a dimension's length label goes, one rule for the canvas and the PDF export (D1):
 *  - the reading angle keeps the text upright (a vertical line reads bottom-to-top);
 *  - the baseline sits `gap` off the dimension line on the READING-UP side -- the side the glyphs grow
 *    toward -- whichever side the offset is on, so the line never strikes through the label;
 *  - a label that does not fit between the ticks (text + `endClear` at each end longer than the line) goes
 *    OUTSIDE: past the line's reading-end, start-anchored, still on the reading-up side. The simplest rule
 *    that keeps it off the ticks and witness lines, and it reads as the continuation of the line.
 * `a1`/`a2` are the dimension line's ends (or A/B for the two-point preview).
 */
export function dimensionLabelLayout(
  a1: Point,
  a2: Point,
  textWidth: number,
  opts: { gap: number; endClear: number; yAxis: YAxis },
): DimensionLabelLayout {
  const dx = a2.x - a1.x;
  const dy = a2.y - a1.y;
  let angle = Math.atan2(dy, dx);
  // y-down: [-90, 90) -- a vertical line reads bottom-to-top; y-up: (-90, 90], the same on paper.
  if (opts.yAxis === 'down') {
    if (angle >= Math.PI / 2) angle -= Math.PI;
    else if (angle < -Math.PI / 2) angle += Math.PI;
  } else if (angle > Math.PI / 2) angle -= Math.PI;
  else if (angle <= -Math.PI / 2) angle += Math.PI;
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  // Glyphs grow toward the text's local "up": -y of the rotated frame in a y-down space, +y in a y-up one.
  const upx = opts.yAxis === 'down' ? uy : -uy;
  const upy = opts.yAxis === 'down' ? -ux : ux;
  const len = Math.hypot(dx, dy);
  if (textWidth + 2 * opts.endClear > len) {
    const end = dx * ux + dy * uy >= 0 ? a2 : a1;
    return {
      x: end.x + ux * opts.endClear + upx * opts.gap,
      y: end.y + uy * opts.endClear + upy * opts.gap,
      angle,
      anchor: 'start',
      outside: true,
    };
  }
  return {
    x: (a1.x + a2.x) / 2 + upx * opts.gap,
    y: (a1.y + a2.y) / 2 + upy * opts.gap,
    angle,
    anchor: 'middle',
    outside: false,
  };
}

/** How far the label reaches off the line at zoom 1 (5 px gap + an 11 px font), in page units. */
export const DIMENSION_LABEL_BAND = 16;

/**
 * The strip along the dimension line, on its reading-up side, where a (centred) label sits at zoom 1 -- in the
 * y-down page space. A tap there is a tap on the label (hit-testing; D3's select tap). A polygon of 4 corners.
 */
export function dimensionLabelBand(a1: Point, a2: Point, depth = DIMENSION_LABEL_BAND): Point[] {
  const { angle } = dimensionLabelLayout(a1, a2, 0, { gap: 0, endClear: 0, yAxis: 'down' });
  const upx = Math.sin(angle) * depth;
  const upy = -Math.cos(angle) * depth;
  return [a1, a2, { x: a2.x + upx, y: a2.y + upy }, { x: a1.x + upx, y: a1.y + upy }];
}

export function dimensionGeometry(p1: Point, p2: Point, p3: Point): DimensionGeometry {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  // Left-hand normal of the p1->p2 direction.
  const nx = -uy;
  const ny = ux;
  const offset = (p3.x - p1.x) * nx + (p3.y - p1.y) * ny;
  return {
    a1: { x: p1.x + nx * offset, y: p1.y + ny * offset },
    a2: { x: p2.x + nx * offset, y: p2.y + ny * offset },
    nx,
    ny,
    offset,
  };
}
